import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ShoppingCart, Trash2, FileText, MapPin } from 'lucide-react';
import api from '../lib/api';

const fmtPrice = (v) => v != null ? `${Number(v).toFixed(2).replace('.', ',')} \u20ac` : '';

/** Floating mini-cart — shows when items exist. Multi-distributor aware. */
export const SimulationMiniCart = () => {
  const { groupsList, totalItems, clearAll, partnerCount } = useCart();
  const navigate = useNavigate();

  if (totalItems === 0) return null;

  const estimated = groupsList.reduce((s, g) => s + g.items.reduce((a, i) => a + (i.base_price || 0) * (i.quantity || 1), 0), 0);

  return (
    <div className="fixed bottom-20 right-6 z-[100]" data-testid="sim-mini-cart">
      <div className="bg-indigo-700 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 min-w-[300px]">
        <div className="relative">
          <ShoppingCart className="h-5 w-5" />
          <span className="absolute -top-2 -right-2 bg-white text-indigo-700 text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center" data-testid="sim-item-count">
            {totalItems}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold truncate">Bon de commande</p>
          <p className="text-[10px] text-indigo-200">{partnerCount} distrib. &middot; {totalItems} produit{totalItems > 1 ? 's' : ''} &middot; ~{fmtPrice(estimated)}</p>
        </div>
        <Button size="sm" variant="secondary" className="h-8 gap-1.5 text-xs font-bold bg-white text-indigo-700 hover:bg-indigo-50"
          onClick={() => navigate('/dashboard/simulation')}
          data-testid="sim-mini-cart-go">
          <FileText className="h-3.5 w-3.5" />
          Pr&eacute;parer
        </Button>
        <button type="button" onClick={clearAll} className="text-indigo-300 hover:text-white transition-colors" data-testid="sim-mini-cart-clear">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};


/** Zone selection modal */
export const ZoneSelectionModal = () => {
  const { zoneModalOpen, closeZoneModal, shippingZone, setShippingZone } = useCart();
  const [zones, setZones] = useState([]);
  const [localZone, setLocalZone] = useState(shippingZone || '');

  useEffect(() => {
    if (zoneModalOpen && zones.length === 0) {
      api.get('/reference/shipping-zones').then(r => setZones(r.data || [])).catch(() => {});
    }
  }, [zoneModalOpen, zones.length]);

  const handleConfirm = () => {
    if (!localZone) return;
    setShippingZone(localZone);
    closeZoneModal();
  };

  return (
    <Dialog open={zoneModalOpen} onOpenChange={(open) => { if (!open) closeZoneModal(); }}>
      <DialogContent className="sm:max-w-md" data-testid="zone-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-indigo-600" /> Zone de livraison</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">S&eacute;lectionnez votre zone pour calculer le franco et les frais de port.</p>
        <Select value={localZone || undefined} onValueChange={setLocalZone}>
          <SelectTrigger data-testid="zone-modal-select"><SelectValue placeholder="Choisir une zone..." /></SelectTrigger>
          <SelectContent>
            {zones.map(z => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button onClick={handleConfirm} disabled={!localZone} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700" data-testid="zone-modal-confirm">
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SimulationMiniCart;
